"use client"
import classNames from 'classnames';
import { useEffect, useRef, useState } from "react";
import CloseSvg from "src/components/svg/CloseSvg";
import { locale } from "@locale";
import styles from './FlyoutPanel.module.css';

//#region [Props]
type FlyoutPanelProps = {
	children: React.ReactNode;
	onAfterCloseComplete?: () => void;
	isClosed?: boolean;
	className?: string;
};
//#endregion

// must match the transition duration in FlyoutPanel.module.css
const CLOSE_ANIMATION_MS = 550;

//#region [Component]
// A modal <dialog>: the browser puts it above the page (top layer), makes the rest of the page inert and keeps focus inside it.
// The opening animation is done by @starting-style in the css, so the panel only needs state for closing.
export default function FlyoutPanel({ children, onAfterCloseComplete, isClosed = false, className }: FlyoutPanelProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [isClosedByUser, setIsClosedByUser] = useState(false);
	const isClosing = isClosedByUser || isClosed;

	useEffect(() => {
		const dialog = dialogRef.current;
		dialog?.showModal();
		return () => dialog?.close();
	}, []);

	useEffect(() => {
		if (isClosing && onAfterCloseComplete) {
			const id = setTimeout(() => onAfterCloseComplete(), CLOSE_ANIMATION_MS);
			return () => {
				clearTimeout(id);
			};
		}
	}, [onAfterCloseComplete, isClosing]);

	const onClose = (event: React.SyntheticEvent<HTMLDialogElement>) => {
		// The close event fires a moment after close(). If the dialog is open again by then, the close was undone and is ignored:
		// in development, Strict Mode unmounts (close()) and remounts (showModal()) right away.
		if (!event.currentTarget.open) {
			// the browser may close the dialog itself, e.g. on a repeated Esc
			setIsClosedByUser(true);
		}
	};

	const onCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
		// Esc: keep the dialog open until the closing animation is done
		event.preventDefault();
		setIsClosedByUser(true);
	};

	return (
		<dialog
			ref={dialogRef}
			className={classNames(styles.flyoutPanel, className, { [styles.closing]: isClosing })}
			onCancel={onCancel}
			onClose={onClose}
			data-scroll-lock
		>
			<button className={styles.closeButton} onClick={() => setIsClosedByUser(true)} aria-label={locale.actions.close}><CloseSvg /></button>
			{children}
		</dialog>
	);
}
//#endregion
